import { FileIcon } from '@sketchnote/icons';
import {
  CheckIcon,
  CloseIcon,
  CreationIcon,
  FilterIcon,
  LessonIcon,
  MoveIcon,
  SearchIcon,
} from '@sketchnote/icons';
import { LoaderXs } from '@sketchnote/shared-components';
import classNames from 'classnames';
import { Emoji } from 'emoji-mart';
import { findAll } from 'highlight-words-core';
import lodash from 'lodash';
import debounce from 'lodash.debounce';
import isDeepEqual from 'lodash.isequal';
import memoizeOne from 'memoize-one';
import router from 'next/router';
import React, { createRef, useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { StandardSnack } from '../../components/snackbar';
import { Avatar, AvatarGroup } from '../avatar';
import { SortActiveText } from '../lessons-list/sub-components';
import { MoreActionMenu } from '../menu';
import { Input } from '../text-input/text-input.component';
import styles from './search.module.css';
import {
  SearchDataProps,
  SearchDialogInputRightProps,
  SearchDialogProps,
  SearchItemProps,
} from './search-dialog.types';

const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  return null;
};

export const SearchDialogInputRight = ({
  onClear,
  onClose,
  isShowClear,
}: SearchDialogInputRightProps) => {
  return (
    <div className="flex items-center">
      {isShowClear ? (
        <span
          role="button"
          className="text-sm text-gray-500 pr-16 border-r-2 mr-17 font-medium"
          onClick={onClear}
          onKeyDown={handleInputKeyDown}
          tabIndex={-2}
        >
          Clear
        </span>
      ) : null}
      <span role="button" onClick={onClose} tabIndex={-1} onKeyDown={handleInputKeyDown}>
        <CloseIcon wrapperClass="align-middle text-xs py-4 -mt-2" />
      </span>
    </div>
  );
};

export const SearchItem = ({
  title = '',
  subTitle = '',
  isShared = false,
  icon: Icon,
  tags = [],
  isDone = false,
  sharedWith = [],
  searchWords = [],
  isActive = true,
  onClick,
  onMouseEnter,
}: SearchItemProps) => {
  const chunks = findAll({
    searchWords: searchWords || [],
    textToHighlight: title,
  });
  const workspaceName =
    subTitle === 'default-workspace' ? (isShared ? 'Shared' : 'Private') : subTitle ?? 'Private';
  const searchHightlightMethod = ({ chunksData }) => (
    <>
      {chunksData.map((chunk) => {
        const { end, highlight, start } = chunk;
        const text = title.substr(start, end - start);
        if (highlight) {
          return <span className="bg-primary-100">{text}</span>;
        } else {
          return <span>{text}</span>;
        }
      })}
    </>
  );
  const TextHighlight = memoizeOne(searchHightlightMethod, isDeepEqual);
  const mode = router.query.mode;
  if (mode === 'w') {
    return (
      <div
        className={classNames({
          'flex py-10 px-16 search-dialog-result-item ': true,
          [`styles['search-dialog-result-item-active'] flex bg-gray-100`]: isActive,
        })}
        role="button"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onKeyDown={handleInputKeyDown}
        tabIndex={0}
      >
        {/* <div className="mr-14">
          <Icon className="text-sm text-gray-500" />
        </div> */}
        <div className={classNames('mr-10 w-18 h-18 items-center')}>
          {Icon?.id && Icon?.type !== 'image' ? (
            <Emoji emoji={Icon?.id} set="apple" size={18} />
          ) : Icon?.id && Icon?.type === 'image' ? (
            <img className=" object-center object-cover w-18 h-18" src={Icon?.id} />
          ) : (
            <span className="bg-gray-100 rounded-sm m-auto items-center">
              <FileIcon className=" text-primary-300 rounded-sm block text-md" />
            </span>
          )}
        </div>
        <div className="flex flex-col -mt-2">
          <h5 className=" leading-20">{title}</h5>
          {subTitle === 'default-workspace' ? (
            <p className="text-gray-600 text-xs flex items-center">{workspaceName}</p>
          ) : (
            <div className="flex flex-row">
              <p className="text-gray-400 text-xs flex items-center">Workspace:&nbsp;</p>
              <p className="text-gray-600 text-xs flex items-center">{workspaceName}</p>
            </div>
          )}
          {tags.length > 0 ? (
            <div className="flex flex-row -ml-1 mt-3">
              {tags.map((item) => (
                <span
                  key={item.name}
                  className="mx-2 px-4 rounded text-xss text-gray-100 pt-2 pb-4"
                  style={{ backgroundColor: `${item.color ?? '#aaaaaa'}` }}
                >
                  {item.name}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  } else {
    return (
      <div
        className={classNames({
          'flex py-16 px-16 search-dialog-result-item ': true,
          [`styles['search-dialog-result-item-active'] flex bg-gray-100`]: isActive,
        })}
        role="button"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onKeyDown={handleInputKeyDown}
        tabIndex={0}
      >
        <div className="mr-14">
          <Icon className="text-sm text-gray-500" />
        </div>
        <div className="flex justify-between w-full">
          <div className="flex flex-col">
            <h5 className="mb-2 leading-20">
              <TextHighlight chunksData={chunks} />
            </h5>
            <p className="text-gray-400 text-xs flex items-center">
              <span className="mr-8">{subTitle}</span>
              {isDone ? <CheckIcon className="text-green-600" /> : null}
              {sharedWith?.length ? (
                <AvatarGroup maxCount={2}>
                  {sharedWith?.map((member) => (
                    <Avatar name={member.name} src={member.photoUrl} size="sm" key={member.name} />
                  ))}
                </AvatarGroup>
              ) : null}
            </p>
          </div>
          <div
            className={classNames({
              'items-center': true,
              [styles['search-dialog-result-item-right-icon']]: true,
            })}
          >
            <MoveIcon className="text-gray-500" wrapperClass="align-middle" />
          </div>
        </div>
      </div>
    );
  }
};

export const NoResultFound = () => {
  return (
    <div className="flex items-center justify-center min-h-200">
      <div className="text-center ">
        <h3 className="text-gray-600 mb-8">No result found</h3>
        <p>Try again using different search terms</p>
      </div>
    </div>
  );
};

export const NoSearchHistory = () => {
  return (
    <div className="flex items-center justify-center min-h-200">
      <div className="text-center ">
        <h3 className="text-gray-600 mb-8">No search history</h3>
        <p>Search using different terms and results will appear here</p>
      </div>
    </div>
  );
};

export const SearchDialog = ({
  getRecentSearches,
  getSearchResult,
  onClose,
  sortMenuOptions,
  filterMenuOptions,
  sortTxt,
  filterText,
  activeSortValue,
  activeFilterValue,
  onClickSortValue,
  onSelectSearchResult,
}: SearchDialogProps): JSX.Element => {
  const [search, setSearch] = React.useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [preSearchData, setPreSearchData] = useState<SearchDataProps>({ total: 0, items: [] });
  const [postSearchData, setPostSearchData] = useState<SearchDataProps>({ total: 0, items: [] });
  const [hasMore, setHasMore] = useState(true);
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [isSortMenuOpened, setIsSortMenuOpened] = React.useState(false);

  const handleCollapseToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleSortOpen = () => {
    setIsSortMenuOpened(true);
  };
  const handleSortClose = () => {
    setIsSortMenuOpened(false);
  };

  const CollapseActionButton = useCallback(
    () => (
      <MoreActionMenu
        menuItems={sortMenuOptions}
        icon={
          <SortActiveText
            isSortMenuOpened={isSortMenuOpened}
            sortTxt={sortTxt}
            activeSortValue={activeSortValue}
          />
        }
        onClickMenuItem={onClickSortValue}
        onOpenMenu={handleSortOpen}
        onCloseMenu={handleSortClose}
      />
    ),
    [isSortMenuOpened]
  );

  const FilterActionButton = useCallback(
    () => (
      <MoreActionMenu
        menuItems={filterMenuOptions}
        icon={
          <SortActiveText
            isSortMenuOpened={isSortMenuOpened}
            sortTxt={filterText}
            activeSortValue={activeFilterValue}
            icon={<FilterIcon className="mr-5" />}
          />
        }
        onClickMenuItem={onClickSortValue}
        onOpenMenu={handleSortOpen}
        onCloseMenu={handleSortClose}
      />
    ),
    [isSortMenuOpened]
  );

  const getSearchData = async (search = '', start = currentPage, changed = false, limit = 10) => {
    const response = await getSearchResult(search, start, limit);
    if (response.items.length === 0) {
      setHasMore(false);
    }
    if (changed) {
      setPostSearchData({ total: 0, items: [] });
    }
    setPostSearchData((prev) => {
      let modified: SearchDataProps = { total: 0, items: [] };
      try {
        if (prev?.items.length < start * limit + limit) {
          modified = lodash.cloneDeep(prev);
          modified.items.push(...response?.items);
          modified.total = response?.total;
        } else {
          modified.items = [...response?.items];
          modified.total = response.total;
        }
      } catch (err) {
        console.log(err);
      }
      return modified;
    });
  };

  const handleFetchMore = async () => {
    await getSearchData(search, currentPage + 1);
    setCurrentPage(currentPage + 1);
  };

  const debouncedSearch = useCallback(
    debounce(async (search) => {
      await getSearchData(search, currentPage, true);
    }, 1000),
    [] // will be created only once initially
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value;
    setSearch((prev) => {
      if (prev.length >= 2) {
        setHasMore(true);
        setCurrentPage(0);
        debouncedSearch(search);
      }
      return search;
    });
  };

  const inputRef = createRef<HTMLInputElement>();

  useEffect(() => {
    inputRef.current?.focus();
    setHasMore(true);
    async function getRecents() {
      const recents = await getRecentSearches();
      setPreSearchData({ total: recents.length, items: [...recents] });
    }

    getRecents();
  }, []);

  const handleClearSearch = () => {
    setSearch('');
    setPostSearchData({ total: 0, items: [] });
  };

  const handleOnMouseEnter = ({ itemIndex, post }: { itemIndex: number; post: boolean }) => {
    setActiveIndex(itemIndex);
  };

  const onClick = ({ itemIndex, post }: { itemIndex: number; post: boolean }) => {
    onSelectSearchResult?.(
      post ? postSearchData.items[itemIndex] : preSearchData.items[itemIndex],
      post
    );
    onClose?.();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const searchData = search.length ? postSearchData : preSearchData;
    let index = 0;
    if (e.key === 'ArrowDown') {
      if (searchData && searchData[activeIndex] === activeIndex + 1) {
        index = 0;
      } else if (searchData && activeIndex + 1 < searchData[activeIndex]) {
        index = activeIndex + 1;
      }
    } else if (e.key === 'ArrowUp') {
      if (activeIndex === 0) {
        if (searchData) {
          index = searchData && searchData.items.length - 1;
        }
      } else if (activeIndex > 0) {
        index = activeIndex - 1;
      }
    }
    setActiveIndex(index);
  };

  const shouldShowSearchBody = isInputFocused || search.length > 0;
  // const shouldShowSearchBody = search.length > 0;
  const searchSplittedWords = search.split(' ');

  const searchIcons = {
    lesson: LessonIcon,
    creation: CreationIcon,
  };

  return (
    <div className="w-full">
      <Input
        placeholder="Search file names or tags"
        inputClassName="placeholder-gray-500 font-medium bg-white py-20 h-55"
        wrapperClassName={`rounded-lg ${shouldShowSearchBody ? 'rounded-b-none' : ''}`}
        field="search"
        type="text"
        leftIcon={<SearchIcon />}
        value={search}
        onChange={handleChange}
        rightIcon={
          <SearchDialogInputRight
            onClear={handleClearSearch}
            onClose={onClose}
            isShowClear={search.length ? true : false}
          />
        }
        inputProps={{
          ref: inputRef,
          onFocus: () => setIsInputFocused(true),
          onBlur: () => setIsInputFocused(true),
          onKeyDown: handleInputKeyDown,
        }}
      />

      {shouldShowSearchBody ? (
        <div className="bg-white rounded-b-lg pt-6">
          <div id="scrollable" className=" max-h-500 overflow-auto search-dialog-result-body ">
            {!search.length ? (
              preSearchData?.items.length ? (
                <div>
                  <div className="mx-16">Your recent searches</div>
                  {preSearchData?.items?.map((item, itemIndex) => (
                    <SearchItem
                      title={item.title}
                      subTitle={item.workspace}
                      isShared={item.isShared}
                      icon={router.query.mode === 'a' ? searchIcons[item.icon] : item.icon}
                      key={item.title}
                      searchWords={searchSplittedWords}
                      onMouseEnter={handleOnMouseEnter.bind(this, {
                        itemIndex: itemIndex,
                        post: false,
                      })}
                      onClick={onClick.bind(this, {
                        itemIndex: itemIndex,
                        post: false,
                      })}
                      isActive={activeIndex === itemIndex}
                    />
                  ))}
                </div>
              ) : (
                <NoSearchHistory />
              )
            ) : null}
            {search.length ? (
              postSearchData?.total > 0 ? (
                <InfiniteScroll
                  dataLength={postSearchData?.items.length || 0}
                  next={handleFetchMore}
                  hasMore={hasMore}
                  loader={
                    <div className="h-50 overflow-hidden">
                      <LoaderXs className="mx-auto text-xss" />
                    </div>
                  }
                  scrollableTarget="scrollable"
                  endMessage={
                    <div className="flex items-center justify-center">
                      <StandardSnack text="That’s all folks! You’ve viewed the full search" />
                    </div>
                  }
                >
                  {/* <div className="flex flex-col"> */}
                  {postSearchData?.items?.map((item, itemIndex) => (
                    <SearchItem
                      title={item.title}
                      subTitle={item.workspace}
                      isShared={item.isShared}
                      icon={router.query.mode === 'a' ? searchIcons[item.icon] : item.icon}
                      tags={item.tags}
                      key={item.title}
                      sharedWith={item.sharedWith}
                      isDone={item.isDone}
                      searchWords={searchSplittedWords}
                      onMouseEnter={handleOnMouseEnter.bind(this, {
                        itemIndex: itemIndex,
                        post: true,
                      })}
                      onClick={onClick.bind(this, {
                        itemIndex: itemIndex,
                        post: true,
                      })}
                      isActive={activeIndex === itemIndex}
                    />
                  ))}
                  {/* </div> */}
                </InfiniteScroll>
              ) : (
                <NoResultFound />
              )
            ) : null}
          </div>
          {search?.length ? (
            <div className="border-t px-16 py-12 text-sm border-gray-200">
              {postSearchData?.total} results
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
